/**
 * WalletActionsContext - Shared context for wallet actions
 *
 * CRITICAL: This file must NOT import wagmi, rainbowkit, viem/chains, or any wallet SDK.
 * This defines the contract that both real and stub providers implement.
 *
 * Architecture:
 * - WalletActionsContext.tsx (this file) - pure types and hook
 * - WalletActionsProviderStub.tsx - stub provider (no wagmi)
 * - WalletActionsProvider.tsx - real provider (has wagmi, lazy loaded)
 */

import { createContext, useContext } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface MintBlockResult {
  blockId: string;
  txHash: `0x${string}`;
}

export interface WalletActionsContextValue {
  // Wallet state
  address: `0x${string}` | undefined;
  isConnected: boolean;

  // USDC balance (raw bigint from contract)
  usdcBalance: bigint | undefined;

  // Mint block
  mintBlock: (contentURI: string, collateralAmount: string) => Promise<MintBlockResult | null>;
  isMinting: boolean;
  mintError: Error | null;
  mintTxHash: `0x${string}` | null;

  // Add collateral (for reactivation)
  addCollateral: (blockId: bigint, amount: string) => Promise<string | null>;
  isAddingCollateral: boolean;

  // Approve treasury
  executeApprove: (amount: string) => Promise<`0x${string}` | null>;

  // Public client for contract reads/simulations
  publicClient: any;

  // Is wallet functionality available?
  walletEnabled: boolean;
}

// ============================================================================
// Context
// ============================================================================

export const WalletActionsContext = createContext<WalletActionsContextValue | null>(null);
WalletActionsContext.displayName = 'WalletActionsContext';

// ============================================================================
// Hook
// ============================================================================

export function useWalletActions(): WalletActionsContextValue {
  const context = useContext(WalletActionsContext);
  if (!context) {
    throw new Error('useWalletActions must be used within a WalletActionsProvider');
  }
  return context;
}
